import React, { useState } from 'react';
import { User } from 'firebase/auth';
import type { LoginCredentials, AdminUser, Department, AppPage, DayOfWeek } from '../../types';
import { Icons } from '../ui/Icons';
import { ALL_PAGES } from '../../types';
import AddAdminModal from './AddAdminModal';
import EditAdminModal from './EditAdminModal';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import ConfirmationModal from '../ui/ConfirmationModal';

interface AdminManagementViewProps {
    currentUser: User | null;
    adminUsers: AdminUser[];
    departments: Department[];
    weekStartDay: DayOfWeek;
    onCreateAdmin: (credentials: LoginCredentials) => Promise<void>;
    onDeleteCurrentUser: () => Promise<void>;
    onUpdateAdminStatus: (uid: string, status: 'active' | 'suspended') => Promise<void>;
    onDeleteAdmin: (uid: string, email: string) => Promise<void>;
    onSaveAdmin: (admin: AdminUser) => Promise<void>;
}

const AdminManagementView: React.FC<AdminManagementViewProps> = ({ currentUser, adminUsers, departments, weekStartDay, onCreateAdmin, onDeleteCurrentUser, onUpdateAdminStatus, onDeleteAdmin, onSaveAdmin }) => {
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);

    const revokeAdminHandler = useDeleteHandler<AdminUser>({
        onDeleteAsync: async (admin) => onDeleteAdmin(admin.uid, admin.email),
        entityName: 'Admin Permissions',
        getDisplayName: (admin) => `for ${admin.email}`,
        getItemId: (admin) => admin.uid,
    });

    return (
        <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Admin Management</h2>
                    <p className="text-gray-500 dark:text-gray-400">Manage administrative access and permissions.</p>
                </div>
                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-all shadow-sm active:scale-95"
                >
                    <Icons.UserPlus className="w-5 h-5" />
                    Add New Admin
                </button>
            </div>

            <div className="overflow-x-auto -mx-8">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700 border-t border-b border-gray-100 dark:border-slate-700/50">
                    <thead className="bg-gray-50/50 dark:bg-slate-900/30 sticky top-0 z-10 shadow-sm">
                        <tr>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Administrator</th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role & Status</th>
                            <th scope="col" className="sticky right-0 z-10 bg-gray-50 dark:bg-slate-900 px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-l border-gray-200 dark:border-slate-700/50">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
                        {adminUsers.map((admin) => (
                            <tr key={admin.uid} className="hover:bg-gray-50/50 dark:hover:bg-slate-700/30 transition-colors group">
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold">
                                            {admin.name.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                {admin.name}
                                                {admin.uid === currentUser.uid && (
                                                    <span className="px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-900/40 text-[10px] text-indigo-600 dark:text-indigo-400 rounded uppercase tracking-wider">You</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{admin.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{admin.role || 'Administrator'}</span>
                                            <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-700 text-[10px] text-gray-600 dark:text-gray-400 rounded font-medium">
                                                {admin.allowedPages ? `${admin.allowedPages.length}/${ALL_PAGES.length} Pages` : 'Full Access'}
                                            </span>
                                        </div>
                                        <span className={`inline-flex items-center w-fit px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                            admin.status === 'active' 
                                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' 
                                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                                        }`}>
                                            {admin.status}
                                        </span>
                                    </div>
                                </td>
                                <td className="sticky right-0 z-10 bg-white dark:bg-slate-800 group-hover:bg-gray-50/50 dark:group-hover:bg-slate-700/30 px-4 py-4 whitespace-nowrap text-right text-sm font-medium border-l border-gray-200 dark:border-slate-700/50">
                                    <div className="flex justify-end gap-2">
                                        {admin.uid !== currentUser.uid ? (
                                            <>
                                                <button 
                                                    onClick={() => setEditingAdmin(admin)} 
                                                    className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                                    title="Edit Admin"
                                                >
                                                    <Icons.Edit className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => onUpdateAdminStatus(admin.uid, admin.status === 'active' ? 'suspended' : 'active')}
                                                    className={`p-2 transition-colors ${
                                                        admin.status === 'active' 
                                                            ? 'text-gray-400 hover:text-amber-600 dark:hover:text-amber-400' 
                                                            : 'text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                                                    }`}
                                                    title={admin.status === 'active' ? 'Suspend Admin' : 'Activate Admin'}
                                                >
                                                    {admin.status === 'active' ? <Icons.UserX className="w-5 h-5" /> : <Icons.UserCheck className="w-5 h-5" />}
                                                </button>
                                                <button 
                                                    onClick={() => revokeAdminHandler.initiateDelete(admin)} 
                                                    className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                                    title="Revoke Permissions"
                                                >
                                                    <Icons.Trash2 className="w-5 h-5" />
                                                </button>
                                            </>
                                        ) : (
                                            <button 
                                                onClick={() => setEditingAdmin(admin)} 
                                                className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                                title="Edit Profile"
                                            >
                                                <Icons.User className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {isAddModalOpen && (
                <AddAdminModal
                    onClose={() => setIsAddModalOpen(false)}
                    onCreateAdmin={onCreateAdmin}
                />
            )}
             {editingAdmin && (
                <EditAdminModal
                    admin={editingAdmin}
                    departments={departments}
                    weekStartDay={weekStartDay}
                    onClose={() => setEditingAdmin(null)}
                    onSave={onSaveAdmin}
                />
            )}
             {revokeAdminHandler.itemToDelete && (
                <ConfirmationModal
                    title="Revoke Admin Permissions"
                    message={`Are you sure you want to revoke admin permissions for ${revokeAdminHandler.itemToDelete.email}? Their login account will NOT be deleted.`}
                    confirmButtonText="Revoke"
                    onConfirm={revokeAdminHandler.confirmDelete}
                    onCancel={revokeAdminHandler.cancelDelete}
                    isConfirming={revokeAdminHandler.isDeleting}
                />
            )}
        </div>
    );
};

export default AdminManagementView;
